/**
 * xmg-actions-first: a native `<details>` whose summary names what is inside and counts it ("Control totals —
 * 424 passed, 3 warnings"). The data of a stage screen sits in these, collapsed, below what needs the operator.
 *
 * `open` is applied as the attribute, so a caller opens a section that needs attention (a failing check) and a
 * later re-render with the same value leaves the operator's own toggling alone. A caller that must open it from
 * elsewhere ("Open the tab to choose for X") passes `open` and `onOpenChange` and owns the state.
 */
export function Disclosure({
  title,
  counts,
  open,
  onOpenChange,
  attention = false,
  asHeading = false,
  testId,
  id,
  className,
  children,
}: {
  title: React.ReactNode;
  /** The counts that make the summary worth reading closed: "424 passed, 3 warnings". */
  counts?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** A red edge: the section holds a failure the operator has to look at. */
  attention?: boolean;
  /** The title is an h2 inside the summary (a section a heading-based reader navigates by). */
  asHeading?: boolean;
  testId?: string;
  id?: string;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  const classes = ["disclosure", attention ? "disclosure--attention" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  return (
    <details
      className={classes}
      open={open}
      id={id}
      data-testid={testId}
      onToggle={onOpenChange === undefined ? undefined : (event) => onOpenChange(event.currentTarget.open)}
    >
      <summary className="disclosure__summary">
        {asHeading ? (
          <h2 className="disclosure__title disclosure__title--heading">{title}</h2>
        ) : (
          <span className="disclosure__title">{title}</span>
        )}
        {counts === undefined || counts === "" ? null : (
          <>
            <span className="disclosure__sep" aria-hidden="true">
              {" — "}
            </span>
            <span className="disclosure__counts">{counts}</span>
          </>
        )}
      </summary>
      <div className="disclosure__body">{children}</div>
    </details>
  );
}
